import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  Suspense,
} from "react";
import { Route, Switch } from "react-router-dom";
import {
  ReferrerDetector,
  ScrollRestoration,
  StateValidator,
  YearSwitchBanner,
  Footer,
  GlobalSessionHandler,
  PrivateRoute,
  LoadingMask,
} from "../components";
import {
  InjectIntlContext,
  cookiesAddedOrRemoved,
  createLocalLogout,
  getLdUser,
  useYearSwitchBanner,
} from "../helpers";
import * as Pages from "../pages";
import messages_en from "../translations/en-US.json";
import messages_es from "../translations/es-MX.json";
import Routes from "./routes";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { AppStore } from "./store";
import RouteToastHandler from "../components/RouteToastHandler";
import { IdleTimeout } from "../components";
import MaxGlobalHeader from "./MaxGlobalHeader";
import { getYearPartInfo } from "../helpers/yearFlagHelpers";
import AnalyticsListeners from "../components/AnalyticsListeners";
import { IntlProvider } from "react-intl";
import { shouldPolyfill as shouldPolyfillRelativeTimeFormat } from "@formatjs/intl-relativetimeformat/should-polyfill";
import { shouldPolyfill as shouldPolyfillPluralRules } from "@formatjs/intl-pluralrules/should-polyfill";
import { config } from "../config";

const AppContent: React.FunctionComponent = () => {
  const flags = useFlags();
  const { state, dispatch } = useContext(AppStore.AppContext);

  const { feMaintenanceMode, tmpFeEnableNewToMedicare } = flags;

  const showYearSwitchBanner = useYearSwitchBanner();

  const { isOutsideOpenEnrollment } = getYearPartInfo(flags);

  // The IntlProvider adds language localization to its children with a default language and traslations map as props
  const messages = {
    en: messages_en,
    es: messages_es,
  };

  useEffect(() => {
    (async () => {
      await polyfill("en");
      await polyfill("es");
    })();
  }, []);

  /**
   * Get the LaunchDarkly client.
   * Provide a callback for updating the user flags if a polled cookie changes.
   */
  const ldClient = useLDClient();

  const updateLdUser = () => {
    const ldUser = getLdUser(state);
    ldClient?.identify(ldUser, undefined, err => {
      if (err) {
        console.error(err);
      }
    });
  };

  const [ldFlagTimeout, setLdFlagTimeout] = useState<
    NodeJS.Timeout | undefined
  >();
  const [couldNotRetrieveLdFlags, setCouldNotRetrieveLdFlags] = useState(false);
  const [ldFlagsAvailable, setLdFlagsAvailable] = useState(false);

  useEffect(() => {
    const ldFlagsAreAvailable = Object.getOwnPropertyNames(flags).length > 0;

    setLdFlagsAvailable(ldFlagsAreAvailable);

    if (!ldFlagsAreAvailable) {
      setLdFlagTimeout(
        setTimeout(() => {
          setCouldNotRetrieveLdFlags(true);
        }, 5000)
      );
    } else {
      if (ldFlagTimeout) {
        clearTimeout(ldFlagTimeout);
        setLdFlagTimeout(undefined);
      }
      setCouldNotRetrieveLdFlags(false);
    }
  }, [flags]);

  /**
   * Watch for an update to cookies and confirm flags have been
   * retrived.
   * If the specified cookie (key) has been added or removed, update flags,
   * accordingly.
   */
  const useCookieChanged = (key: string) => {
    const ref = useRef(document.cookie);
    useEffect(() => {
      if (couldNotRetrieveLdFlags) {
        return;
      }
      const cookiesChanged = cookiesAddedOrRemoved(key, ref.current)();
      if (cookiesChanged) {
        updateLdUser();
      }
      ref.current = document.cookie;
    }, [document.cookie, couldNotRetrieveLdFlags]);
    return ref.current;
  };

  useCookieChanged("sid");

  /**
   * Watch for an update to `state.beneficiary` and confirm flags have been
   * retrived.
   * If beneficiary data is available, update flags, accordingly.
   */
  useEffect(() => {
    if (couldNotRetrieveLdFlags || !state.beneficiary) {
      return;
    }
    updateLdUser();
    if (config.DEBUG) {
      createLocalLogout(dispatch);
    }
  }, [couldNotRetrieveLdFlags, state.beneficiary]);

  return (
    <IntlProvider locale={state.language} messages={messages[state.language]}>
      <InjectIntlContext>
        <MaxGlobalHeader>
          <StateValidator>
            <ScrollRestoration>
              <ReferrerDetector>
                <IdleTimeout>
                  <AnalyticsListeners>
                    <RouteToastHandler>
                      <div className="ds-base">
                        {showYearSwitchBanner && <YearSwitchBanner />}
                        <Suspense
                          fallback={
                            <LoadingMask
                              loading={true}
                              message={messages[state.language]["app.loading"]}
                            />
                          }
                        >
                          {ldFlagsAvailable || couldNotRetrieveLdFlags ? (
                            <div className="AppContent">
                              <main className="app__main e2e-app-main">
                                {feMaintenanceMode ||
                                couldNotRetrieveLdFlags ? (
                                  <Pages.MaintenancePage />
                                ) : (
                                  <GlobalSessionHandler>
                                    <Switch>
                                      <Route
                                        path={Routes.searchResults}
                                        component={Pages.SearchResultsPage}
                                      />
                                      <Route
                                        path={Routes.sanctionedPlans}
                                        component={Pages.SanctionedPlansPage}
                                      />
                                      <Route
                                        path={Routes.drugSearchPreferences}
                                        component={
                                          Pages.DrugSearchPreferencesPage
                                        }
                                      />
                                      {!isOutsideOpenEnrollment && (
                                        <Route
                                          path={Routes.yearOverYear}
                                          component={Pages.PlanComparePage}
                                        />
                                      )}
                                      <Route
                                        path={Routes.comparePlans}
                                        component={Pages.PlanComparePage}
                                      />
                                      <Route
                                        path={Routes.planDetails}
                                        component={Pages.PlanDetailsPage}
                                      />
                                      <Route
                                        path={Routes.managePrescriptions}
                                        component={
                                          Pages.ManagePrescriptionsPage
                                        }
                                      />
                                      <Route
                                        path={Routes.prescriptionsList}
                                        component={
                                          Pages.ManagePrescriptionsPage
                                        }
                                      />
                                      <Route
                                        path={Routes.managePharmacies}
                                        component={Pages.ManagePharmaciesPage}
                                      />
                                      <Route
                                        path={Routes.inNetworkPharmacy}
                                        exact
                                        render={props => (
                                          <Pages.SelectPharmaciesPage
                                            isInNetwork={true}
                                            {...props}
                                          />
                                        )}
                                      />
                                      <Route
                                        path={Routes.pharmacy}
                                        component={Pages.SelectPharmaciesPage}
                                      />
                                      <Route
                                        path={Routes.questionRouting}
                                        component={Pages.QuestionRoutingPage}
                                      />
                                      <Route
                                        path={Routes.lisQuestions}
                                        component={Pages.LISQuestionsPage}
                                      />
                                      <Route
                                        path={Routes.enroll}
                                        component={Pages.EnrollmentFormPage}
                                      />
                                      <Route
                                        path={Routes.csrLanding}
                                        component={Pages.CSRLandingPage}
                                      />
                                      <Route
                                        path={Routes.pdePage}
                                        component={Pages.PDEPage}
                                      />
                                      <Route
                                        path={Routes.version}
                                        component={Pages.VersionPage}
                                      />
                                      <Route
                                        path={Routes.planPreview}
                                        component={Pages.PlanPreviewPage}
                                      />
                                      <Route
                                        path={Routes.coverageWizard.options}
                                        component={
                                          Pages.CoverageWizardOptionsPage
                                        }
                                      />
                                      <Route
                                        path={Routes.coverageWizard.info}
                                        component={Pages.CoverageWizardInfoPage}
                                      />
                                      <Route
                                        path={Routes.coverageWizard.landingPage}
                                        component={
                                          Pages.CoverageWizardLandingPage
                                        }
                                      />
                                      <Route
                                        path={Routes.pace.plans}
                                        component={Pages.PacePlansPage}
                                      />
                                      <Route
                                        path={Routes.pace.landingPage}
                                        component={Pages.PaceLandingPage}
                                      />
                                      <Route
                                        path={Routes.pap.plans}
                                        component={Pages.PapPlansPage}
                                      />
                                      <Route
                                        path={Routes.spap.plans}
                                        component={Pages.SpapPlansPage}
                                      />
                                      <Route
                                        path={Routes.spap.landingPage}
                                        component={Pages.SpapLandingPage}
                                      />
                                      <PrivateRoute
                                        path={Routes.summary.lisQuestions}
                                      >
                                        <Pages.LISQuestionsPage
                                          isFromSummaryPage={true}
                                        />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        path={Routes.summary.managePharmacies}
                                      >
                                        <Pages.ManagePharmaciesPage
                                          isFromSummaryPage={true}
                                        />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        path={
                                          Routes.summary.managePrescriptions
                                        }
                                      >
                                        <Pages.ManagePrescriptionsPage
                                          isFromSummaryPage={true}
                                        />
                                      </PrivateRoute>

                                      <PrivateRoute
                                        path={Routes.summary.selectPharmacies}
                                      >
                                        <Pages.SelectPharmaciesPage
                                          isFromSummaryPage={true}
                                        />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        path={
                                          Routes.summary.drugSearchPreferences
                                        }
                                      >
                                        <Pages.DrugSearchPreferencesPage
                                          isFromSummaryPage={true}
                                        />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        path={Routes.summary.pdePage}
                                      >
                                        <Pages.PDEPage
                                          isFromSummaryPage={true}
                                        />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        path={Routes.summary.searchResults}
                                      >
                                        <Pages.SearchResultsPage />
                                      </PrivateRoute>
                                      <PrivateRoute
                                        path={Routes.summary.landingPage}
                                      >
                                        <Pages.SummaryPage />
                                      </PrivateRoute>
                                      <Route
                                        path={Routes.pap.landingPage}
                                        component={Pages.PapLandingPage}
                                      />
                                      <Route
                                        path={Routes.logout}
                                        component={Pages.LogoutPage}
                                      />
                                      <Route
                                        path={Routes.medigap.plans}
                                        component={Pages.MedigapPlans}
                                      />
                                      <Route
                                        path={Routes.medigap.planDetails}
                                        component={Pages.MedigapPlanDetailsPage}
                                      />
                                      <Route
                                        path={Routes.medigap.policies}
                                        component={Pages.MedigapPoliciesPage}
                                      />
                                      <Route
                                        path={Routes.medigap.landingPage}
                                        component={Pages.MedigapLandingPage}
                                      />
                                      {tmpFeEnableNewToMedicare && (
                                        <Route
                                          path={Routes.newToMedicare}
                                          component={Pages.NewToMedicarePage}
                                        />
                                      )}
                                      {tmpFeEnableNewToMedicare && (
                                        <Route
                                          path={Routes.findPlansNow}
                                          component={Pages.FindPlansNowPage}
                                        />
                                      )}
                                      <Route component={Pages.LandingPage} />
                                    </Switch>
                                  </GlobalSessionHandler>
                                )}
                              </main>
                              {!location.hash.includes(
                                Routes.planPreview.replace("/:id", "")
                              ) && <Footer />}
                            </div>
                          ) : (
                            <Pages.LoadingPage />
                          )}
                        </Suspense>
                      </div>
                    </RouteToastHandler>
                  </AnalyticsListeners>
                </IdleTimeout>
              </ReferrerDetector>
            </ScrollRestoration>
          </StateValidator>
        </MaxGlobalHeader>
      </InjectIntlContext>
    </IntlProvider>
  );
};

async function polyfill(locale: string) {
  const unsupportedRelativeTimeLocale =
    shouldPolyfillRelativeTimeFormat(locale);
  const unsupportedPluralRulesLocale = shouldPolyfillPluralRules(locale);

  if (unsupportedRelativeTimeLocale) {
    // Load the polyfill 1st BEFORE loading data
    await import("@formatjs/intl-relativetimeformat/polyfill-force");
    await import(
      `@formatjs/intl-relativetimeformat/locale-data/${unsupportedRelativeTimeLocale}`
    );
  }

  if (unsupportedPluralRulesLocale) {
    // Load the polyfill 1st BEFORE loading data
    await import("@formatjs/intl-pluralrules/polyfill-force");
    await import(
      `@formatjs/intl-pluralrules/locale-data/${unsupportedRelativeTimeLocale}`
    );
  }
}

export default AppContent;
